import React, { useState, useEffect } from "react";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { useParams } from "react-router-dom";
import AllocateLocationModal from "./AllocateLocationModal";
import EditBatchModal from "./EditBatchModal"; // Import the new Edit modal
import ConfirmDeleteModal from "./ConfirmDeleteModal"; // Import the delete confirmation modal
import { FiArrowLeft } from "react-icons/fi";
import { useDeleteItem } from "../../_helper/actions/mutation/items";
import { toast } from "react-toastify";

const ReceiptDetail = () => {
  const { id } = useParams();

  // Fetch receipt data from the API
  const { data } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getInventoryReceipt&id=${id}&receipt_data=True`
  );

  const dlt_mutation = useDeleteItem(ApiEndPoint.PHARMACY);

  // Local state for batches to allow in-place updates
  const [batches, setBatches] = useState([]);
  // For tracking the batch for which location is to be allocated
  const [selectedBatchIndex, setSelectedBatchIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  // Control modal visibility for allocation
  const [isAllocateModalOpen, setIsAllocateModalOpen] = useState(false);
  // Control modal visibility for editing
  const [isEditBatchModalOpen, setIsEditBatchModalOpen] = useState(false);
  const [selectedEditBatchIndex, setSelectedEditBatchIndex] = useState(null);
  // Control modal visibility for deletion confirmation
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDeleteBatchIndex, setSelectedDeleteBatchIndex] =
    useState(null);

  // Initialize local batches state when data loads
  useEffect(() => {
    if (data?.batches) {
      setBatches(data.batches);
    }
  }, [data]);

  // Calculate tax summary based on batches
  const calculateTaxes = (batches) => {
    let subtotal = 0;
    let totalIGST = data?.igst || 0;
    let totalCGST = data?.cgst || 0;
    let totalSGST = data?.sgst || 0;

    batches?.forEach((batch) => {
      subtotal += batch.price * batch.received_quantity;
    });

    return {
      subtotal,
      totalIGST,
      totalCGST,
      totalSGST,
      totalTax: totalIGST + totalCGST + totalSGST,
      grandTotal: subtotal + totalIGST + totalCGST + totalSGST,
    };
  };

  const taxSummary = calculateTaxes(batches);

  // Open the allocation modal for a given batch (passing its item id)
  const handleAllocateClick = (index, item_id) => {
    setSelectedBatchIndex(index);
    setSelectedItem(item_id);
    setIsAllocateModalOpen(true);
  };

  // Open the edit modal for a given batch
  const handleEditClick = (index) => {
    setSelectedEditBatchIndex(index);
    setIsEditBatchModalOpen(true);
  };

  // Open the delete confirmation modal for a given batch
  const handleDeleteClick = (index) => {
    setSelectedDeleteBatchIndex(index);
    setIsDeleteModalOpen(true);
  };

  // Confirm deletion of the selected batch using its id
  const confirmDelete = (batchId) => {
    dlt_mutation.mutate(
      { data: { id: batchId, action: "delBatch" } },
      {
        onSuccess: () => {
          toast.success("Batch deleted successfully!");
          setIsDeleteModalOpen(false);
          setSelectedDeleteBatchIndex(null);
        },
      }
    );
  };

  // Define tax summary items with custom colors
  const taxSummaryItems = [
    {
      label: "Total CGST",
      value: `₹${data?.cgst}`,
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
    },
    {
      label: "Total SGST",
      value: `₹${data?.sgst}`,
      bgColor: "bg-green-100",
      textColor: "text-green-800",
    },
    {
      label: "Total IGST",
      value: `₹${data?.igst}`,
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
    },
    {
      label: "Total Tax",
      value: `₹${data?.total_gst}`,
      bgColor: "bg-red-100",
      textColor: "text-red-800",
    },
    {
      label: "Grand Total",
      value: `₹${data?.grand_total}`,
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-800",
    },
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Enhanced Header Section */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => window.history.back()}
          className="flex items-center px-4 py-2 text-black after:focus:outline-none"
        >
          <FiArrowLeft className="mr-2 text-xl" />
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-800">Receipt Details</h1>
        {/* Spacer div to balance the flex layout */}
        <div className="w-12" />
      </div>

      {/* Main Card */}
      <div className="p-6 ">
        {/* Compact Section: Invoice & Supplier Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Invoice Details */}
          <section className="bg-gray-50 p-4 rounded border">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-1 mb-2">
              Invoice Details
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "No", value: data?.invoice_no || "N/A" },
                {
                  label: "Date",
                  value: data?.invoice_date
                    ? new Date(data.invoice_date).toLocaleDateString()
                    : "N/A",
                },
                { label: "Chalan", value: data?.chalan_no || "N/A" },
                { label: "PO", value: data?.purchase_order_number || "N/A" },
                {
                  label: "Receipt",
                  value: data?.receipt_date
                    ? new Date(data.receipt_date).toLocaleDateString()
                    : "N/A",
                },
                {
                  label: "Purchase",
                  value: data?.purchase_date
                    ? new Date(data.purchase_date).toLocaleDateString()
                    : "N/A",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col">
                  <span className="text-xs text-gray-500">{item.label}</span>
                  <span className="text-sm text-gray-800">{item.value}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Supplier Details */}
          <section className="bg-gray-50 p-4 rounded border">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-1 mb-2">
              Supplier Details
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Name", value: data?.vendor_id?.vendor_name || "N/A" },
                { label: "GSTIN", value: data?.vendor_id?.gstin || "N/A" },
                {
                  label: "Phone",
                  value: data?.vendor_id?.contact_phone || "N/A",
                },
                {
                  label: "Address",
                  value: data?.vendor_id?.address_line_1 || "N/A",
                },
                { label: "State", value: data?.vendor_id?.state || "N/A" },
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col">
                  <span className="text-xs text-gray-500">{item.label}</span>
                  <span className="text-sm text-gray-800">{item.value}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Invoice Items */}
        <section className="mb-4 bg-gray-50 p-4 rounded border">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-1 mb-2 ">
            Invoice Items
          </h3>
          <div className="overflow-x-auto">
            {batches && batches.length > 0 ? (
              <table className="min-w-full table-auto text-sm">
                <thead className="bg-gray-200">
                  <tr>
                    {[
                      "Item Name",
                      "HSN",
                      "Batch No",
                      "Available Qty",
                      "Recived Qty",
                      "Batch Price",
                      "MRP",
                      "Rcvd Date",
                      "Expiry",
                      "Location",
                      "Action",
                    ].map((header, idx) => (
                      <th
                        key={idx}
                        className="px-2 py-2 text-gray-700 uppercase tracking-wider whitespace-nowrap text-center"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300">
                  {batches.map((batch, index) => (
                    <tr key={index} className="hover:bg-gray-100">
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {batch?.item_id?.items_name || "N/A"}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {batch.hns_code || "N/A"}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {batch.batch_no}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {batch.quantity}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {batch.received_quantity}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        ₹{batch.batch_price}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        ₹{batch.price}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {new Date(batch.receive_date).toLocaleDateString()}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {new Date(batch.expiry_date).toLocaleDateString()}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        {batch.location && batch.location.length > 0
                          ? batch.location.map((loc) => loc.name).join(", ")
                          : "N/A"}
                      </td>
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          {(!batch.location || batch.location.length === 0) && (
                            <button
                              onClick={() =>
                                handleAllocateClick(index, batch?.item_id?.id)
                              }
                              className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded transition duration-150 text-xs"
                            >
                              Allocate
                            </button>
                          )}
                          <button
                            onClick={() => handleEditClick(index)}
                            className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition duration-150 text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(index)}
                            className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded transition duration-150 text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-8 text-center text-gray-500">
                No invoice items available.
              </div>
            )}
          </div>
        </section>

        {/* Tax Summary */}
        <section className="mt-4 bg-gray-50 p-4 rounded border">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-1 mb-2">
            Tax Summary
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {taxSummaryItems.map((item, idx) => (
              <div
                key={idx}
                className={`p-2 rounded border flex flex-col items-center ${item.bgColor}`}
              >
                <span className="text-xs">{item.label}</span>
                <span className={`text-base font-semibold ${item.textColor}`}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Allocate Location Modal */}
      {isAllocateModalOpen && (
        <AllocateLocationModal
          item_id={selectedItem}
          isOpen={isAllocateModalOpen}
          onClose={() => setIsAllocateModalOpen(false)}
          onSave={(location) => {
            const updatedBatches = [...batches];
            updatedBatches[selectedBatchIndex].location = [
              { id: location.value, name: location.label },
            ];
            setBatches(updatedBatches);
            setIsAllocateModalOpen(false);
          }}
          batch={batches[selectedBatchIndex]}
        />
      )}

      {/* Edit Batch Modal */}
      {isEditBatchModalOpen && (
        <EditBatchModal
          batch={batches[selectedEditBatchIndex]}
          isOpen={isEditBatchModalOpen}
          onClose={() => setIsEditBatchModalOpen(false)}
          onSave={(updatedData) => {
            const updatedBatches = [...batches];
            updatedBatches[selectedEditBatchIndex] = {
              ...updatedBatches[selectedEditBatchIndex],
              batch_no: updatedData.batch_no,
              price: updatedData.price,
            };
            setBatches(updatedBatches);
            setIsEditBatchModalOpen(false);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedDeleteBatchIndex !== null && (
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          batchName={
            `${batches[selectedDeleteBatchIndex]?.batch_no} batch` ||
            "this batch"
          }
          batchId={batches[selectedDeleteBatchIndex]?.id}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default ReceiptDetail;
